<script lang="ts" setup>
import type { Component, Document, Page } from '@bloomreach/spa-sdk'
import { toRefs } from 'vue'
import type { TRefPage } from '@/components/PlusBlog/type'
import { useImagesetRef } from '@/composables/blog/useImagesetRef'

interface TBLogPostItem {
    url: string
    src: string
    alt: string
    categories: [string]
    publicationdate: string
    title: string
    id: string
    mainImage: { $ref: string }
}

interface TEmbeddedMediaImagesItem {
    altText: string
    imagelink: {
        $ref: string
    }
    type: string
}

/**
 * SDX
 */
const props = defineProps<{
    component: Component
    page: Page
}>()

const { component, page } = toRefs(props)

const documentRef = computed(() => component.value.getModels().document)
const documentPage = computed(() => documentRef.value && page.value.getContent<Document>(documentRef.value))
const data = computed(() => documentPage.value?.getData())

const {
    publicationdate,
    title,
    embeddedMedia,
    introduction,
    socialMediaItems,
    mainImage,
    author,
    categories,
    content,
    id,
    tags,
    relatedBlogposts,
    downloadableAssets,
} = data.value

const mainImgaeRef = computed(() => mainImage?.$ref && page.value.getContent<Document>(mainImage.$ref))
const heroImage = useImageRef(mainImgaeRef)

/**
 * relatedBlogposts
 */

const arrayBlogPostToShow = computed<TBLogPostItem[]>(() => {
    return relatedBlogposts?.map((element: TRefPage) => {
        const documentPost = page.value.getContent<Document>(element?.$ref)
        const data = documentPost?.getData() as any // "as any" dla dostępności właściwości

        const contentDataPost: TBLogPostItem = {
            url: data.url || '',
            src: '', // przypisane poniżej
            alt: data.alt || '',
            categories: data.categories?.join(', ') || [],
            publicationdate: data.publicationdate || '',
            title: data.title || '',
            id: data.id || '',
            mainImage: data.mainImage || { $ref: '' },
        }

        const documentPostImg = page.value.getContent<Document>(contentDataPost.mainImage?.$ref)
        const postImg = useImageRef(documentPostImg)
        return {
            ...contentDataPost,
            src: postImg?.value?.src,
            alt: postImg?.value?.alt,
        }
    })
})

/**
 * Author
 */

const documentAuthor = computed(() => author?.$ref && page.value.getContent<Document>(author.$ref))
const dataAuthor = computed(() => documentAuthor.value?.getData())

const { articlesLink = null, fullname, image = null, role = '' } = dataAuthor?.value || {}

const documentAuthorImg = computed(() => image?.$ref && page.value.getContent<Document>(image.$ref))
const authorImg = useImageRef(documentAuthorImg)

/**
 * Embedded Media
 */
const { type } = embeddedMedia || { type: null }
const setEmbeddedMedia = computed(() => {
    switch (type) {
        case 'video':
            return {
                src: embeddedMedia.assetlink.links.site.href,
            }
        case 'picture': {
            const embeddedMediaImg =
                embeddedMedia.imagelink?.$ref && page.value.getContent<Document>(embeddedMedia.imagelink.$ref)
            const imgData = useImageRef(embeddedMediaImg)
            return {
                alt: embeddedMedia.altText ? embeddedMedia.altText : imgData.value.alt,
                src: imgData?.value?.src,
                'img-class': 'mx-auto rounded-lg w-full',
            }
        }
        case 'code': {
            return embeddedMedia
        }
        case 'gallery': {
            const images = embeddedMedia.images.map((image: TEmbeddedMediaImagesItem, index: number) => {
                const embeddedMediaImg = image.imagelink?.$ref && page.value.getContent<Document>(image.imagelink.$ref)
                const srcData = useImagesetRef(embeddedMediaImg)
                return {
                    id: Number(index),
                    alt: image.altText,
                    src: srcData,
                }
            })
            return { images }
        }
        default:
            return {}
    }
})
</script>

<template>
    <article
        :id="id"
        class="page-plus mx-auto mt-12 max-w-screen-lg px-3 lg:mt-16 lg:px-0"
    >
        <header class="mb-6 text-center lg:mb-12">
            <h1
                class="mb-2 text-3xl font-bold lg:mb-3 lg:text-4xl"
                v-html="title"
            />
            <p class="flex items-center justify-center gap-2 text-xs text-plus-gray-500 lg:text-sm">
                {{ categories?.join(', ') || '' }} <span class="block size-1 rounded-full bg-plus-gray-500" />
                {{ publicationdate }}
            </p>
        </header>
        <AtomImage
            v-if="mainImage"
            :src="heroImage.src"
            :alt="heroImage.alt"
            img-class="mx-auto mb-8 w-full rounded-lg lg:mb-12"
        />
        <section class="mx-auto max-w-3.5xl">
            <p
                class="mb-8 text-lg font-bold lg:mb-12 lg:text-2xl"
                v-html="introduction"
            />

            <PlusBlogCustomMedia
                v-if="embeddedMedia && type"
                :media-type="type"
                :media-data="setEmbeddedMedia"
            />
            <div
                class="c-focus copy-post mb-8 lg:mb-12"
                v-html="content?.value"
            />
        </section>
    </article>
    <PlusBlogAuthorSection
        v-if="author"
        :author-img="authorImg.src"
        :alt-img="authorImg.alt"
        :full-name="fullname"
        :role="role"
        :articles-link="articlesLink"
        :tags="tags"
    />

    <section
        v-if="socialMediaItems || downloadableAssets"
        class="mx-3 flex justify-center lg:mx-0"
    >
        <div class="flex w-full max-w-3.5xl flex-wrap justify-between gap-8 border-t border-plus-gray py-8 lg:py-12">
            <PlusBlogSocialMedia
                v-if="socialMediaItems"
                :data="socialMediaItems"
            />
            <PlusBlogDownloadMaterials
                v-if="downloadableAssets"
                :data="downloadableAssets"
                class="mr-20"
            />
        </div>
    </section>

    <!-- relatedBlogposts -->
    <section
        v-if="relatedBlogposts"
        class="px-3 md:px-0"
    >
        <h2 class="text-heading-sm md:text-heading-lg mb-6 text-center font-bold text-plus-primary md:mb-12">
            Podobne artykuły
        </h2>
        <div class="mx-auto mb-12 max-w-6.5xl px-3 lg:px-0">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <PlusBlogListItem
                    v-for="el in arrayBlogPostToShow"
                    :key="el.id"
                    :data="el"
                    tabindex="0"
                />
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.copy-post {
    &:deep(h2) {
        @apply font-bold mb-4 mt-12 text-lg lg:text-2xl;
    }
    &:deep(h3) {
        @apply font-bold mb-4 mt-12 text-base lg:text-xl;
    }
    &:deep(p) {
        @apply mb-4 text-base md:text-lg;
        line-height: 2rem !important;
        img {
            @apply mx-auto;
        }
    }
    &:deep(a) {
        @apply text-plus-blue-700 underline;
    }
    &:deep(ul) {
        list-style: unset;
        @apply mb-4 text-base md:text-lg pl-10;
    }
    &:deep(ol) {
        list-style: decimal;
        @apply mb-4 text-base md:text-lg pl-10;
    }
}
</style>
